import { METRICS_SORT_OPTION, type MetricGroup } from "@/metrics/grouping"
import {
  resolveMetricDescription,
  resolveMetricModality,
  type useMetricDirectory,
} from "@/metrics/registry"
import { TagDotsPreview } from "@/tags"
import { cn, CopyableId, Tooltip } from "@coval/ui"
import { Ban, type LucideIcon } from "lucide-react"

import type { CsvColumnDef as ColumnDef } from "@/utils/csv/tableCsvColumns"
import { resolveMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import type { MetricCatalogItem } from "./metricCatalogItem"

// Column widths, trimmed from the metrics list page's set to what a picker needs
// (no dates or creator) so the description still reads inside a dialog.
const NAME_COLUMN_WIDTH = 240
const NAME_COLUMN_MIN_WIDTH = 160
const DESCRIPTION_COLUMN_WIDTH = 420
const DESCRIPTION_COLUMN_MIN_WIDTH = 200
const TYPE_COLUMN_WIDTH = 110
// "Audio" / "Text" / "Trace" — sized to its longest label, and fixed so it never
// stretches into whitespace.
const MODALITY_COLUMN_WIDTH = 96
const CATEGORY_COLUMN_WIDTH = 150
const TAGS_COLUMN_WIDTH = 120

/**
 * The picker's row model, matching the metrics list page's: a section band whose
 * children are a stand-in column-header row followed by that section's metrics.
 */
export type MetricPickerRowNode =
  | {
      kind: "group"
      id: string
      group: MetricGroup<MetricCatalogItem>
      icon: LucideIcon
      children: MetricPickerRowNode[]
    }
  | { kind: "columnHeader"; id: string }
  | { kind: "metric"; id: string; metric: MetricCatalogItem }

interface BuildMetricPickerColumnsParams {
  byManager: ReturnType<typeof useMetricDirectory>["byManager"]
  getMetricType: (metric: MetricCatalogItem) => string
  metricErrors: Record<string, string>
  // Grouping by type already shows the type as the section header, so the column
  // surfaces the functional category in that slot instead.
  showTypeColumn: boolean
  tagColorMap?: Record<string, string | null | undefined>
}

export function buildMetricPickerColumns({
  byManager,
  getMetricType,
  metricErrors,
  tagColorMap,
}: BuildMetricPickerColumnsParams): ColumnDef<MetricPickerRowNode>[] {
  return [
    {
      id: METRICS_SORT_OPTION.NAME,
      csvValue: (row) =>
        row.kind === "metric" ? row.metric.metric_display_name : null,
      header: "Name",
      variant: "name",
      width: NAME_COLUMN_WIDTH,
      minWidth: NAME_COLUMN_MIN_WIDTH,
      sortable: true,
      cell: (row) => {
        if (row.kind !== "metric") {
          return null
        }
        const displayName = resolveMetricDisplayName(
          row.metric.metric_display_name,
          row.metric.metric_name,
        )
        // A metric the setup rules out keeps its row, but reads as out of play:
        // the name drops to the muted ink the row's other cells use, and the
        // reason sits under it. The reason also carries a tooltip, since a
        // caller's own message can be longer than this column is wide.
        const errorMessage = metricErrors[row.metric.metric_name]
        return (
          <div className="flex min-w-0 flex-col">
            <span
              className={cn(
                "min-w-0 truncate font-semibold",
                errorMessage && "text-coval-text-muted",
              )}
              title={displayName}
            >
              {displayName}
            </span>
            {errorMessage ? (
              <Tooltip content={errorMessage} className="max-w-sm">
                <span className="flex min-w-0 items-center gap-1 text-xs text-coval-danger-text">
                  <Ban className="size-3 shrink-0" aria-hidden />
                  <span className="min-w-0 truncate">{errorMessage}</span>
                </span>
              </Tooltip>
            ) : null}
          </div>
        )
      },
    },
    {
      id: "description",
      csvValue: (row) =>
        row.kind === "metric" ? row.metric.description : null,
      header: "Description",
      width: DESCRIPTION_COLUMN_WIDTH,
      minWidth: DESCRIPTION_COLUMN_MIN_WIDTH,
      cell: (row) => {
        if (row.kind !== "metric") {
          return null
        }
        const description =
          resolveMetricDescription(
            byManager[row.metric.metric_manager ?? ""],
            row.metric.description,
          ) || "No description"
        return (
          <Tooltip content={description} className="max-w-sm">
            <span className="line-clamp-2 min-w-0 text-sm text-coval-text-muted">
              {description}
            </span>
          </Tooltip>
        )
      },
    },
    {
      id: "type",
      csvValue: (row) =>
        row.kind === "metric" ? getMetricType(row.metric) : null,
      header: "Type",
      width: TYPE_COLUMN_WIDTH,
      flex: false,
      cell: (row: MetricPickerRowNode) => {
        if (row.kind !== "metric") {
          return null
        }
        const type = getMetricType(row.metric)
        return (
          <span
            className="min-w-0 truncate whitespace-nowrap text-coval-text-muted"
            title={type}
          >
            {type}
          </span>
        )
      },
    },
    {
      id: "category",
      csvValue: (row) =>
        row.kind === "metric"
          ? byManager[row.metric.metric_manager ?? ""]?.category ||
            row.metric.category ||
            "Other"
          : null,
      header: "Category",
      width: CATEGORY_COLUMN_WIDTH,
      cell: (row: MetricPickerRowNode) => {
        if (row.kind !== "metric") {
          return null
        }
        // Prefer the catalog's functional category; fall back to the
        // per-instance value. `||` (not `??`) so an empty string counts as
        // missing.
        const category =
          byManager[row.metric.metric_manager ?? ""]?.category ||
          row.metric.category ||
          "Other"
        return (
          <span
            className="min-w-0 truncate whitespace-nowrap text-coval-text-muted"
            title={category}
          >
            {category}
          </span>
        )
      },
    },
    {
      id: "modality",
      csvValue: (row) =>
        row.kind === "metric"
          ? resolveMetricModality(byManager[row.metric.metric_manager ?? ""])
              ?.label
          : null,
      header: "Reads",
      width: MODALITY_COLUMN_WIDTH,
      flex: false,
      cell: (row) => {
        if (row.kind !== "metric") {
          return null
        }
        // What the metric scores — the audio, the transcript, or the run's
        // trace. A custom metric the catalog doesn't describe carries no value
        // rather than a guessed one.
        const modality = resolveMetricModality(
          byManager[row.metric.metric_manager ?? ""],
        )
        if (!modality) {
          return <span className="text-coval-text-muted">–</span>
        }
        return (
          <Tooltip content={modality.description}>
            <span className="min-w-0 truncate whitespace-nowrap text-coval-text-muted">
              {modality.label}
            </span>
          </Tooltip>
        )
      },
    },
    {
      id: "tags",
      csvValue: (row) =>
        row.kind === "metric" ? row.metric.tags?.join(", ") : null,
      header: "Tags",
      width: TAGS_COLUMN_WIDTH,
      align: "right",
      flex: false,
      cell: (row) =>
        row.kind !== "metric" ? null : (
          <div className="flex min-w-0 justify-end">
            <TagDotsPreview
              tags={row.metric.tags ?? []}
              tagColorMap={tagColorMap}
            />
          </div>
        ),
    },
    {
      id: "id",
      csvValue: (row) => (row.kind === "metric" ? row.metric.id : null),
      header: "ID",
      variant: "id",
      pinned: "right",
      cell: (row) =>
        row.kind !== "metric" || !row.metric.id ? null : (
          <CopyableId id={row.metric.id} label="Copy Metric ID" />
        ),
    },
  ]
}
